/**
 * Triagem compartilhada por caso: patient_cpf (só dígitos) + assistance_id.
 * O vínculo fica em support_center_assistance_triages; appointments.triage_id não existe mais.
 */
import { supabase } from '../lib/supabase'
import { fetchAllByRange, SUPABASE_IN_CHUNK_SIZE } from '../lib/supabaseFetchAll'
import type { SupportCenterTriageStatus } from '../types/supportCenterTriage'

/**
 * Pares por cláusula `or(and(...))`. Menor que o chunk de `.in()` porque o SELECT
 * de agendamento é longo e a URL do PostgREST tem limite.
 */
const PAIR_FILTER_CHUNK = 20

export type CaseTriageRef = {
  triage_id: string
  /** null quando deleted_at está preenchido (sem triagem vinculada para UI). */
  status: SupportCenterTriageStatus | null
  triage_date: string | null
  deleted_at: string | null
  form_key: string | null
}

function onlyDigits(value: string): string {
  return value.replace(/\D/g, '')
}

export function caseTriageKey(patientCpf: string, assistanceId: string): string {
  return `${onlyDigits(patientCpf)}|${assistanceId.trim()}`
}

/** Normaliza status do banco / rótulos da API (Reprovada, Concluída, etc.). */
export function normalizeTriageStatus(raw: unknown): SupportCenterTriageStatus | null {
  if (raw == null) return null
  const value = String(raw)
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
  if (value === 'pendente') return 'pendente'
  if (value === 'concluida') return 'concluida'
  if (value === 'reprovado' || value === 'reprovada') return 'reprovado'
  if (value === 'cancelado') return 'cancelado'
  if (value === 'nao_realizado') return 'nao_realizado'
  return null
}

/** Mapeia data.triage.status da resposta de reagendamento (rótulos da API). */
export function mapApiTriageStatusLabel(raw: unknown): SupportCenterTriageStatus | null {
  if (raw == null || raw === '') return null
  const label = String(raw).trim()
  if (!label) return null
  const normalized = normalizeTriageStatus(label)
  if (normalized) return normalized
  // Fallback da API: qualquer valor não mapeado → Pendente
  return 'pendente'
}

function isAppointmentVisiblyOpen(row: {
  status?: unknown
  deleted_at?: unknown
}): boolean {
  const status = String(row.status ?? '')
  if (status !== 'marcado' && status !== 'reagendado') return false
  if (row.deleted_at) return false
  return true
}

/**
 * Entre agendamentos abertos do caso: o mais próximo no futuro;
 * se não houver futuro, o aberto mais recente.
 */
export function pickOpenAppointmentRow(
  rows: Record<string, unknown>[],
  now: Date = new Date()
): Record<string, unknown> | null {
  const open = rows.filter((row) => isAppointmentVisiblyOpen(row))
  if (open.length === 0) return null

  const nowMs = now.getTime()
  const withTime = open.map((row) => {
    const scheduled = String(row.scheduled_at ?? '')
    const parsed = new Date(scheduled.includes('T') ? scheduled : scheduled.replace(' ', 'T'))
    const ms = Number.isNaN(parsed.getTime()) ? null : parsed.getTime()
    return { row, ms }
  })

  const future = withTime
    .filter((item) => item.ms != null && item.ms >= nowMs)
    .sort((a, b) => (a.ms as number) - (b.ms as number))
  if (future.length > 0) return future[0].row

  const pastOrUnknown = withTime.sort((a, b) => {
    if (a.ms == null && b.ms == null) return 0
    if (a.ms == null) return 1
    if (b.ms == null) return -1
    return (b.ms as number) - (a.ms as number)
  })
  return pastOrUnknown[0]?.row ?? null
}

function chunkList<T>(items: T[], size: number): T[][] {
  const chunks: T[][] = []
  for (let i = 0; i < items.length; i += size) {
    chunks.push(items.slice(i, i + size))
  }
  return chunks
}

/** Dígitos e máscara 000.000.000-00: o vínculo pode estar gravado nos dois formatos. */
function cpfLookupValues(digits: string): string[] {
  if (digits.length !== 11) return [digits]
  const formatted = digits.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4')
  return [digits, formatted]
}

/** Aspas quando o valor tem `.` ou vírgula — senão o PostgREST quebra o operador. */
function postgrestEq(column: string, value: string): string {
  if (/[,".:()]/.test(value)) {
    return `${column}.eq."${value.replace(/"/g, '""')}"`
  }
  return `${column}.eq.${value}`
}

function pairAndFilter(patientCpf: string, assistanceId: string): string {
  return cpfLookupValues(patientCpf)
    .map(
      (cpf) =>
        `and(${postgrestEq('patient_cpf', cpf)},${postgrestEq('assistance_id', assistanceId)})`
    )
    .join(',')
}

type AssistanceTriageLink = {
  patient_cpf: string | null
  assistance_id: string | null
  triage_id: string | null
}

async function fetchAllPages<T>(
  fetchPage: (from: number, to: number) => PromiseLike<{ data: T[] | null; error: { message: string } | null }>
): Promise<T[]> {
  const { data, error } = await fetchAllByRange(fetchPage)
  if (error) throw new Error(error)
  return data
}

/**
 * Vínculos só dos pares pedidos (CPF dígitos ou mascarado + assistência), em lotes,
 * com páginas até esgotar o limite de 1000 linhas do PostgREST.
 */
async function fetchAssistanceLinksByPairs(
  pairs: Array<{ patient_cpf: string; assistance_id: string }>
): Promise<AssistanceTriageLink[]> {
  const unique = new Map<string, { patient_cpf: string; assistance_id: string }>()
  for (const pair of pairs) {
    const cpf = onlyDigits(pair.patient_cpf)
    const assistanceId = pair.assistance_id.trim()
    if (!cpf || !assistanceId) continue
    unique.set(caseTriageKey(cpf, assistanceId), { patient_cpf: cpf, assistance_id: assistanceId })
  }

  const rows: AssistanceTriageLink[] = []
  for (const chunk of chunkList([...unique.values()], PAIR_FILTER_CHUNK)) {
    const orFilter = chunk.map((pair) => pairAndFilter(pair.patient_cpf, pair.assistance_id)).join(',')
    const page = await fetchAllPages<AssistanceTriageLink>((from, to) =>
      supabase
        .from('support_center_assistance_triages')
        .select('patient_cpf, assistance_id, triage_id')
        .or(orFilter)
        .order('triage_id', { ascending: true })
        .order('patient_cpf', { ascending: true })
        .order('assistance_id', { ascending: true })
        .range(from, to)
    )
    rows.push(...page)
  }
  return rows
}

async function fetchAssistanceLinksByTriageIds(triageIds: string[]): Promise<AssistanceTriageLink[]> {
  const rows: AssistanceTriageLink[] = []
  for (const chunk of chunkList(triageIds, SUPABASE_IN_CHUNK_SIZE)) {
    const page = await fetchAllPages<AssistanceTriageLink>((from, to) =>
      supabase
        .from('support_center_assistance_triages')
        .select('patient_cpf, assistance_id, triage_id')
        .in('triage_id', chunk)
        .order('triage_id', { ascending: true })
        .order('patient_cpf', { ascending: true })
        .order('assistance_id', { ascending: true })
        .range(from, to)
    )
    rows.push(...page)
  }
  return rows
}

/**
 * Resolve o mapa caso → triagem para um lote de pares patient_cpf + assistance_id.
 * Não lista todos os vínculos da assistência: cada lote pede só os pares,
 * aceitando CPF só com dígitos ou mascarado.
 */
export async function fetchCaseTriageMap(
  pairs: Array<{ patient_cpf?: string | null; assistance_id?: string | null }>
): Promise<Map<string, CaseTriageRef>> {
  const result = new Map<string, CaseTriageRef>()
  const wantedKeys = new Set<string>()
  const wantedPairs: Array<{ patient_cpf: string; assistance_id: string }> = []

  for (const pair of pairs) {
    const cpf = onlyDigits(String(pair.patient_cpf ?? ''))
    const assistanceId = String(pair.assistance_id ?? '').trim()
    if (!cpf || !assistanceId) continue
    const key = caseTriageKey(cpf, assistanceId)
    if (wantedKeys.has(key)) continue
    wantedKeys.add(key)
    wantedPairs.push({ patient_cpf: cpf, assistance_id: assistanceId })
  }

  if (wantedKeys.size === 0) return result

  const links = await fetchAssistanceLinksByPairs(wantedPairs)

  const triageIds = new Set<string>()
  const linkByKey = new Map<string, string>()
  for (const link of links) {
    const cpf = onlyDigits(String(link.patient_cpf ?? ''))
    const assistanceId = String(link.assistance_id ?? '').trim()
    const triageId = String(link.triage_id ?? '').trim()
    if (!cpf || !assistanceId || !triageId) continue
    const key = caseTriageKey(cpf, assistanceId)
    if (!wantedKeys.has(key)) continue
    linkByKey.set(key, triageId)
    triageIds.add(triageId)
  }

  if (triageIds.size === 0) return result

  const triages: Array<{
    id: string
    status: string | null
    triage_date: string | null
    deleted_at: string | null
    form_key: string | null
  }> = []
  for (const chunk of chunkList([...triageIds], SUPABASE_IN_CHUNK_SIZE)) {
    const page = await fetchAllPages<{
      id: string
      status: string | null
      triage_date: string | null
      deleted_at: string | null
      form_key: string | null
    }>((from, to) =>
      supabase
        .from('support_center_triages')
        .select('id, status, triage_date, deleted_at, form_key')
        .in('id', chunk)
        .order('id', { ascending: true })
        .range(from, to)
    )
    triages.push(...page)
  }

  const triageById = new Map<string, CaseTriageRef>()
  for (const row of triages) {
    const id = String(row.id ?? '').trim()
    if (!id) continue
    const deletedAt =
      typeof row.deleted_at === 'string' && row.deleted_at.trim()
        ? row.deleted_at
        : null
    const triageDateRaw = row.triage_date
    const triage_date =
      typeof triageDateRaw === 'string' && triageDateRaw.trim()
        ? triageDateRaw.trim().slice(0, 10)
        : null
    // Registro ativo sem status reconhecido → pendente (mesmo fallback da API).
    const status = deletedAt
      ? null
      : normalizeTriageStatus(row.status) ?? ('pendente' as SupportCenterTriageStatus)
    triageById.set(id, {
      triage_id: id,
      status,
      triage_date: deletedAt ? null : triage_date,
      deleted_at: deletedAt,
      form_key: typeof row.form_key === 'string' ? row.form_key.trim() || null : null,
    })
  }

  for (const [key, triageId] of linkByKey) {
    const ref = triageById.get(triageId)
    if (ref) {
      result.set(key, ref)
    } else {
      result.set(key, {
        triage_id: triageId,
        status: 'pendente',
        triage_date: null,
        deleted_at: null,
        form_key: null,
      })
    }
  }

  return result
}

/**
 * Anexa triage_id e objeto triage (id, status, triage_date, deleted_at) em cada linha
 * de agendamento, usando o vínculo do caso. Remove embeds legados se existirem.
 */
export async function attachCaseTriageToAppointmentRows(
  rows: Record<string, unknown>[]
): Promise<Record<string, unknown>[]> {
  return attachCaseTriageDetailsToAppointmentRows(rows)
}

/**
 * Anexa a triagem do caso; com `triageSelect` carrega campos extras (catálogos, etc.).
 */
export async function attachCaseTriageDetailsToAppointmentRows(
  rows: Record<string, unknown>[],
  triageSelect?: string
): Promise<Record<string, unknown>[]> {
  if (rows.length === 0) return rows
  const caseMap = await fetchCaseTriageMap(
    rows.map((row) => ({
      patient_cpf: String(row.patient_cpf ?? ''),
      assistance_id:
        (row.assistance_id as string | null) ??
        (() => {
          const assistance = Array.isArray(row.assistance) ? row.assistance[0] : row.assistance
          if (assistance && typeof assistance === 'object') {
            return String((assistance as { id?: string }).id ?? '') || null
          }
          return null
        })(),
    }))
  )

  let triageDetails = new Map<string, Record<string, unknown>>()
  if (triageSelect) {
    const ids = [...caseMap.values()]
      .filter((ref) => !ref.deleted_at)
      .map((ref) => ref.triage_id)
    triageDetails = await fetchTriagesByIds(ids, triageSelect)
  }

  return rows.map((row) => {
    const cpf = onlyDigits(String(row.patient_cpf ?? ''))
    let assistanceId = String(row.assistance_id ?? '').trim()
    if (!assistanceId) {
      const assistance = Array.isArray(row.assistance) ? row.assistance[0] : row.assistance
      if (assistance && typeof assistance === 'object') {
        assistanceId = String((assistance as { id?: string }).id ?? '').trim()
      }
    }
    const ref =
      cpf && assistanceId ? caseMap.get(caseTriageKey(cpf, assistanceId)) : undefined
    if (!ref) {
      return {
        ...row,
        triage_id: null,
        triage: null,
      }
    }
    if (ref.deleted_at) {
      return {
        ...row,
        triage_id: null,
        triage: {
          id: ref.triage_id,
          status: null,
          triage_date: null,
          deleted_at: ref.deleted_at,
          form_key: ref.form_key,
        },
      }
    }
    const details = triageDetails.get(ref.triage_id)
    return {
      ...row,
      assistance_id: assistanceId || ((row.assistance_id as string | null) ?? null),
      triage_id: ref.triage_id,
      triage: details ?? {
        id: ref.triage_id,
        status: ref.status,
        triage_date: ref.triage_date,
        deleted_at: null,
        form_key: ref.form_key,
      },
    }
  })
}

/**
 * Carrega triagens por id com um SELECT customizado (ex.: volumetria com catálogos).
 */
export async function fetchTriagesByIds(
  triageIds: string[],
  select: string
): Promise<Map<string, Record<string, unknown>>> {
  const map = new Map<string, Record<string, unknown>>()
  const unique = [...new Set(triageIds.map((id) => id.trim()).filter(Boolean))]
  if (unique.length === 0) return map

  for (const chunk of chunkList(unique, SUPABASE_IN_CHUNK_SIZE)) {
    const page = await fetchAllPages<Record<string, unknown>>((from, to) =>
      supabase
        .from('support_center_triages')
        .select(select)
        .in('id', chunk)
        .order('id', { ascending: true })
        .range(from, to)
        .then((result) => ({
          data: (result.data ?? null) as Record<string, unknown>[] | null,
          error: result.error,
        }))
    )
    for (const row of page) {
      const id = String(row.id ?? '').trim()
      if (id) map.set(id, row)
    }
  }
  return map
}

/**
 * Dado triage_ids, resolve o vínculo do caso e retorna o melhor agendamento aberto
 * (ou o primeiro disponível se não houver aberto) por triagem.
 */
export async function resolveAppointmentsByTriageIds(
  triageIds: string[],
  appointmentSelect: string
): Promise<Map<string, Record<string, unknown> | null>> {
  const result = new Map<string, Record<string, unknown> | null>()
  const unique = [...new Set(triageIds.map((id) => id.trim()).filter(Boolean))]
  for (const id of unique) result.set(id, null)
  if (unique.length === 0) return result

  const links = await fetchAssistanceLinksByTriageIds(unique)

  type CasePair = { patient_cpf: string; assistance_id: string; triage_id: string }
  const pairs: CasePair[] = []
  for (const link of links) {
    const triageId = String(link.triage_id ?? '').trim()
    const cpf = onlyDigits(String(link.patient_cpf ?? ''))
    const assistanceId = String(link.assistance_id ?? '').trim()
    if (!triageId || !cpf || !assistanceId) continue
    pairs.push({ patient_cpf: cpf, assistance_id: assistanceId, triage_id: triageId })
  }

  if (pairs.length === 0) return result

  const appointments: Record<string, unknown>[] = []
  const uniquePairs = new Map<string, { patient_cpf: string; assistance_id: string }>()
  for (const pair of pairs) {
    uniquePairs.set(caseTriageKey(pair.patient_cpf, pair.assistance_id), {
      patient_cpf: pair.patient_cpf,
      assistance_id: pair.assistance_id,
    })
  }
  for (const chunk of chunkList([...uniquePairs.values()], PAIR_FILTER_CHUNK)) {
    const orFilter = chunk.map((pair) => pairAndFilter(pair.patient_cpf, pair.assistance_id)).join(',')
    const page = await fetchAllPages<Record<string, unknown>>((from, to) =>
      supabase
        .from('support_center_appointments')
        .select(appointmentSelect)
        .or(orFilter)
        .order('scheduled_at', { ascending: true })
        .order('id', { ascending: true })
        .range(from, to)
        .then((result) => ({
          data: (result.data ?? null) as Record<string, unknown>[] | null,
          error: result.error,
        }))
    )
    appointments.push(...page)
  }

  const appointmentsByCase = new Map<string, Record<string, unknown>[]>()
  for (const row of appointments) {
    const cpf = onlyDigits(String(row.patient_cpf ?? ''))
    const assistanceId = String(row.assistance_id ?? '').trim()
    if (!cpf || !assistanceId) continue
    const key = caseTriageKey(cpf, assistanceId)
    const list = appointmentsByCase.get(key) ?? []
    list.push(row)
    appointmentsByCase.set(key, list)
  }

  for (const pair of pairs) {
    const key = caseTriageKey(pair.patient_cpf, pair.assistance_id)
    const caseRows = appointmentsByCase.get(key) ?? []
    const exact = caseRows
      .filter(
        (row) =>
          onlyDigits(String(row.patient_cpf ?? '')) === pair.patient_cpf &&
          String(row.assistance_id ?? '').trim() === pair.assistance_id
      )
      .sort((a, b) => {
        const as = String(a.scheduled_at ?? '')
        const bs = String(b.scheduled_at ?? '')
        if (as < bs) return -1
        if (as > bs) return 1
        return String(a.id ?? '').localeCompare(String(b.id ?? ''))
      })
    const picked = pickOpenAppointmentRow(exact) ?? exact[0] ?? null
    result.set(pair.triage_id, picked)
  }

  return result
}

/**
 * Confirma que o agendamento pertence ao caso da triagem (patient_cpf + assistance_id).
 */
export async function assertAppointmentBelongsToTriageCase(
  triageId: string,
  appointmentId: string
): Promise<{ id: string; status: string; scheduled_at: string }> {
  const triage = triageId.trim()
  const appointment = appointmentId.trim()
  if (!triage) throw new Error('Triagem inválida.')
  if (!appointment) throw new Error('Agendamento de contexto não informado.')

  const { data: link, error: linkError } = await supabase
    .from('support_center_assistance_triages')
    .select('patient_cpf, assistance_id')
    .eq('triage_id', triage)
    .maybeSingle()

  if (linkError) throw linkError
  if (!link) throw new Error('Caso da triagem não encontrado.')

  const cpf = onlyDigits(String(link.patient_cpf ?? ''))
  const assistanceId = String(link.assistance_id ?? '').trim()

  const { data: row, error } = await supabase
    .from('support_center_appointments')
    .select('id, status, scheduled_at, patient_cpf, assistance_id, deleted_at')
    .eq('id', appointment)
    .maybeSingle()

  if (error) throw error
  if (!row) throw new Error('Agendamento não encontrado.')

  if (
    onlyDigits(String(row.patient_cpf ?? '')) !== cpf ||
    String(row.assistance_id ?? '').trim() !== assistanceId
  ) {
    throw new Error('Este agendamento não pertence ao caso desta triagem.')
  }

  return {
    id: String(row.id),
    status: String(row.status ?? ''),
    scheduled_at: String(row.scheduled_at ?? ''),
  }
}
